import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { isValidEmail } from '@lender/utils';
import { Box, Button, Typography } from '@mui/material';
import { TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { closeDialogAction } from 'store/global/actions';

import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';

const FixEmailDialog = ({ currentEmail = '', onSubmit }) => {
  const dispatch = useDispatch();

  const [email, setEmail] = useState(currentEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedEmail = email.trim();
  const isValid = isValidEmail(trimmedEmail) && trimmedEmail !== currentEmail.trim();

  const onChangeEmail = useCallback(event => setEmail(event.target.value), []);

  const onClose = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const onConfirm = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const succeeded = await onSubmit(trimmedEmail);

      if (succeeded) {
        dispatch(closeDialogAction());
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, onSubmit, trimmedEmail]);

  return (
    <Box className="flex flex-col items-stretch gap-12">
      <Typography color="text.secondary" variant="regular14">
        Correct the recipient email and resend the signing notification. The document is not
        regenerated.
      </Typography>
      <TextFieldCDS
        fullWidth
        dataTestId={DisclosureDeliveryTestIds.FIX_EMAIL_INPUT}
        label="Recipient email"
        name="recipient-email"
        value={email}
        onChange={onChangeEmail}
      />
      <Box className="pt-8 self-end flex gap-8">
        <Button
          data-testid={DisclosureDeliveryTestIds.FIX_EMAIL_CANCEL_BUTTON}
          disabled={isSubmitting}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={DisclosureDeliveryTestIds.FIX_EMAIL_SUBMIT_BUTTON}
          disabled={!isValid || isSubmitting}
          variant="contained"
          onClick={onConfirm}
        >
          Save & resend
        </Button>
      </Box>
    </Box>
  );
};

FixEmailDialog.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  currentEmail: PropTypes.string,
};

export default FixEmailDialog;
